import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { sendWithdrawalEmail, sendBankCredentialsEmail } from '@/lib/email';
import type { AdminClient } from '@/mocks/adminClients';
import type { AdminRequest } from '@/mocks/adminRequests';
import type { AdminLog } from '@/mocks/adminLogs';
import { generateBankLogin, generateId, generateSimulatedCard, generateValidationCode } from '@/utils/withdrawal';
import { CODE_MAX_DIGITS, CODE_MIN_DIGITS, CODE_VALIDITY_HOURS, DEMO_CODE_DIGITS } from '@/config/withdrawal';
import type { Bank } from '@/hooks/useBanks';
import { PROCESSING_ESTIMATE, type CodeStatus, type LogLevel, type RequestStatus } from '@/config/admin';

export type NewClientInput = {
  fullName: string;
  email: string;
  phone: string;
  balance: number;
  currency: string;
  status: AdminClient['status'];
  memberSince: string;
  accessCode: string;
  bankLogin: string;
  bankPassword: string;
  cardBrand: string;
  cardNumber: string;
  cardExpiry: string;
  cardCvv: string;
  maxBankAttempts: number;
  maxCardAttempts: number;
};

export type BlockedIp = {
  id: string;
  ip: string;
  reason: string;
  blockedAt: string;
  blockedBy: string;
};

/** Configuration du lien unique de collecte des codes de validation. */
export type CodeLink = {
  token: string;
  amount: number;
  currency: string;
  active: boolean;
  /** Nombre de chiffres du code de validation (entre 4 et 8). */
  codeDigits: number;
};

export type ActivateResult =
  | { ok: true }
  | { ok: false; reason: 'mismatch' | 'expired' | 'used' };

type Row = Record<string, unknown>;

function nowIso(): string {
  return new Date().toISOString();
}

/**
 * Les erreurs de l'API ne sont pas toujours des instances d'Error
 * (elles arrivent souvent sous forme d'objet { message, details, hint, code }).
 * On extrait donc le vrai message pour ne pas afficher un texte générique trompeur.
 */
function extractError(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message;
  if (typeof err === 'string' && err.trim()) return err;
  if (err && typeof err === 'object') {
    const maybe = err as { message?: string; details?: string; hint?: string; code?: string };
    const parts = [maybe.message, maybe.details, maybe.hint].filter(
      (part): part is string => typeof part === 'string' && part.trim().length > 0,
    );
    if (parts.length > 0) return parts.join(' - ');
    if (maybe.code) return `Erreur ${maybe.code}`;
  }
  return fallback;
}

function mapClientRow(row: Row): AdminClient {
  return {
    id: row.id as string,
    accessCode: row.access_code as string,
    fullName: row.full_name as string,
    email: row.email as string,
    phone: (row.phone as string) ?? '',
    balance: Number(row.balance),
    currency: row.currency as string,
    status: row.status as AdminClient['status'],
    memberSince: row.member_since as string,
    bankLogin: (row.bank_login as string) ?? '',
    bankPassword: (row.bank_password as string) ?? '',
    credentialsGeneratedAt: (row.credentials_generated_at as string) ?? '',
    credentialsSentAt: (row.credentials_sent_at as string) ?? '',
    cardBrand: (row.card_brand as string) ?? '',
    cardNumber: (row.card_number as string) ?? '',
    cardExpiry: (row.card_expiry as string) ?? '',
    cardCvv: (row.card_cvv as string) ?? '',
    cardGeneratedAt: (row.card_generated_at as string) ?? '',
    maxBankAttempts: Number(row.max_bank_attempts ?? 2) || 2,
    maxCardAttempts: Number(row.max_card_attempts ?? 2) || 2,
  };
}

function mapRequestRow(row: Row): AdminRequest {
  return {
    id: row.id as string,
    reference: row.reference as string,
    clientId: (row.client_id as string) ?? '',
    clientCode: (row.client_code as string) ?? '',
    clientName: (row.client_name as string) ?? '',
    fullName: (row.full_name as string) ?? '',
    bankName: (row.bank_name as string) ?? '',
    iban: (row.iban as string) ?? '',
    phone: (row.phone as string) ?? '',
    email: (row.email as string) ?? '',
    amount: Number(row.amount),
    currency: (row.currency as string) ?? 'XPF',
    createdAt: (row.created_at as string) ?? '',
    status: row.status as RequestStatus,
    code: (row.code as string) ?? '',
    codeStatus: (row.code_status as CodeStatus) ?? 'sent',
    codeGeneratedAt: (row.code_generated_at as string) ?? '',
    codeExpiresAt: (row.code_expires_at as string) ?? '',
    codeUsedAt: (row.code_used_at as string) ?? undefined,
    activatedAt: (row.activated_at as string) ?? undefined,
    activatedBy: (row.activated_by as string) ?? undefined,
    processingEstimate: (row.processing_estimate as string) ?? PROCESSING_ESTIMATE,
    authMethod: (row.auth_method as string) ?? undefined,
  };
}

function mapLogRow(row: Row): AdminLog {
  return {
    id: row.id as string,
    timestamp: row.timestamp as string,
    ip: (row.ip as string) ?? '',
    user: (row.actor as string) ?? '',
    action: row.action as string,
    reference: (row.reference as string) ?? undefined,
    level: row.level as LogLevel,
    userAgent: (row.user_agent as string) ?? undefined,
  };
}

function mapBankRow(row: Row): Bank {
  return {
    id: row.id as string,
    name: row.name as string,
    short: (row.short as string) ?? '',
    sortOrder: Number(row.sort_order ?? 0),
  };
}

function mapIpRow(row: Row): BlockedIp {
  return {
    id: row.id as string,
    ip: row.ip as string,
    reason: (row.reason as string) ?? '',
    blockedAt: (row.blocked_at as string) ?? '',
    blockedBy: (row.blocked_by as string) ?? '',
  };
}

function isCodeExpired(request: AdminRequest): boolean {
  return new Date(request.codeExpiresAt).getTime() < Date.now();
}

const MANAGER_IP = '203.0.113.18';

export function useAdminData() {
  const [clients, setClients] = useState<AdminClient[]>([]);
  const [requests, setRequests] = useState<AdminRequest[]>([]);
  const [logs, setLogs] = useState<AdminLog[]>([]);
  const [blockedIps, setBlockedIps] = useState<BlockedIp[]>([]);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [codeLink, setCodeLink] = useState<CodeLink | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    const [clientsRes, requestsRes, logsRes, ipsRes, banksRes, codeLinkRes] = await Promise.all([
      supabase.from('clients').select('*').order('member_since', { ascending: false }),
      supabase.from('withdrawal_requests').select('*').order('created_at', { ascending: false }),
      supabase.from('activity_logs').select('*').order('timestamp', { ascending: false }),
      supabase.from('blocked_ips').select('*').order('blocked_at', { ascending: false }),
      supabase.from('banks').select('*').order('sort_order', { ascending: true }),
      supabase.from('code_links').select('*').eq('id', 'default').maybeSingle(),
    ]);

    if (clientsRes.error) throw clientsRes.error;
    if (requestsRes.error) throw requestsRes.error;
    if (logsRes.error) throw logsRes.error;
    if (ipsRes.error) throw ipsRes.error;
    if (banksRes.error) throw banksRes.error;
    if (codeLinkRes.error) throw codeLinkRes.error;

    setClients((clientsRes.data ?? []).map(mapClientRow));
    setRequests((requestsRes.data ?? []).map(mapRequestRow));
    setLogs((logsRes.data ?? []).map(mapLogRow));
    setBlockedIps((ipsRes.data ?? []).map(mapIpRow));
    setBanks((banksRes.data ?? []).map(mapBankRow));
    if (codeLinkRes.data) {
      const linkRow = codeLinkRes.data as Row;
      const digits = Number(linkRow.code_digits);
      setCodeLink({
        token: (linkRow.token as string) ?? '',
        amount: Number(linkRow.amount) || 0,
        currency: (linkRow.currency as string) ?? 'XPF',
        active: Boolean(linkRow.active),
        codeDigits:
          Number.isFinite(digits) && digits >= CODE_MIN_DIGITS && digits <= CODE_MAX_DIGITS
            ? digits
            : DEMO_CODE_DIGITS,
      });
    }
  }, []);

  /**
   * Supprime automatiquement les « codes morts » : codes expirés ou invalidés,
   * ainsi que les demandes dont le code a expiré sans jamais être activé.
   */
  const purgeDeadCodesInternal = useCallback(async (): Promise<number> => {
    const nowIso = new Date().toISOString();
    const [expired, invalidated, abandoned] = await Promise.all([
      supabase.from('withdrawal_requests').delete({ count: 'exact' }).eq('code_status', 'expired'),
      supabase.from('withdrawal_requests').delete({ count: 'exact' }).eq('code_status', 'invalidated'),
      supabase
        .from('withdrawal_requests')
        .delete({ count: 'exact' })
        .is('activated_at', null)
        .lt('code_expires_at', nowIso)
        .in('status', ['new', 'registered', 'code_sent', 'awaiting_activation']),
    ]);
    return (expired.count ?? 0) + (invalidated.count ?? 0) + (abandoned.count ?? 0);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Nettoyage automatique des codes morts avant l'affichage.
      await purgeDeadCodesInternal().catch(() => undefined);
      await fetchAll();
    } catch (err) {
      setError(extractError(err, 'Erreur de chargement des données'));
    } finally {
      setLoading(false);
    }
  }, [fetchAll, purgeDeadCodesInternal]);

  useEffect(() => {
    load();
  }, [load]);

  const addLog = useCallback(async (entry: Omit<AdminLog, 'id' | 'timestamp'>) => {
    await supabase.from('activity_logs').insert({
      id: generateId(),
      timestamp: nowIso(),
      ip: entry.ip,
      actor: entry.user,
      action: entry.action,
      reference: entry.reference ?? null,
      level: entry.level,
    });
  }, []);

  const activateRequest = useCallback(
    async (requestId: string, codeInput: string, managerEmail: string): Promise<ActivateResult> => {
      const request = requests.find((entry) => entry.id === requestId);
      if (!request) {
        return { ok: false, reason: 'mismatch' };
      }

      if (request.codeStatus === 'used') {
        return { ok: false, reason: 'used' };
      }

      if (isCodeExpired(request) || request.codeStatus === 'expired') {
        return { ok: false, reason: 'expired' };
      }

      if (codeInput.trim() !== request.code) {
        return { ok: false, reason: 'mismatch' };
      }

      const activatedAt = nowIso();

      try {
        const { error } = await supabase
          .from('withdrawal_requests')
          .update({
            status: 'activation_validated',
            code_status: 'used',
            code_used_at: activatedAt,
            activated_at: activatedAt,
            activated_by: managerEmail,
          })
          .eq('id', requestId);

        if (error) throw error;

        await addLog({
          ip: MANAGER_IP,
          user: managerEmail,
          action: 'Activation du virement validée',
          reference: request.reference,
          level: 'info',
        });

        await sendWithdrawalEmail(requestId, 'activation_confirmed');

        await fetchAll();
        return { ok: true };
      } catch (err) {
        setError(extractError(err, 'Erreur lors de l\'activation'));
        return { ok: false, reason: 'mismatch' };
      }
    },
    [requests, addLog, fetchAll],
  );

  const renewCode = useCallback(
    async (requestId: string, managerEmail: string): Promise<AdminRequest | null> => {
      const request = requests.find((entry) => entry.id === requestId);
      if (!request) return null;

      const newCode = generateValidationCode(codeLink?.codeDigits ?? DEMO_CODE_DIGITS);
      const generatedAt = nowIso();
      const expiresAt = new Date(Date.now() + CODE_VALIDITY_HOURS * 60 * 60 * 1000).toISOString();

      try {
        const { error } = await supabase
          .from('withdrawal_requests')
          .update({
            code: newCode,
            code_status: 'sent',
            code_generated_at: generatedAt,
            code_expires_at: expiresAt,
            code_used_at: null,
          })
          .eq('id', requestId);

        if (error) throw error;

        await addLog({
          ip: MANAGER_IP,
          user: managerEmail,
          action: 'Nouveau code de validation généré',
          reference: request.reference,
          level: 'info',
        });

        await fetchAll();

        return {
          ...request,
          code: newCode,
          codeStatus: 'sent',
          codeGeneratedAt: generatedAt,
          codeExpiresAt: expiresAt,
          codeUsedAt: undefined,
        };
      } catch (err) {
        setError(extractError(err, 'Erreur lors du renouvellement'));
        return null;
      }
    },
    [requests, codeLink, addLog, fetchAll],
  );

  const invalidateCode = useCallback(
    async (requestId: string, managerEmail: string): Promise<void> => {
      const request = requests.find((entry) => entry.id === requestId);
      if (!request) return;

      try {
        await supabase
          .from('withdrawal_requests')
          .update({ code_status: 'invalidated' })
          .eq('id', requestId);

        await addLog({
          ip: MANAGER_IP,
          user: managerEmail,
          action: 'Code de validation invalidé',
          reference: request.reference,
          level: 'warning',
        });

        await fetchAll();
      } catch (err) {
        setError(extractError(err, 'Erreur lors de l\'invalidation'));
      }
    },
    [requests, addLog, fetchAll],
  );

  const updateRequestStatus = useCallback(
    async (requestId: string, status: RequestStatus, managerEmail: string): Promise<void> => {
      const request = requests.find((entry) => entry.id === requestId);
      if (!request) return;

      try {
        await supabase.from('withdrawal_requests').update({ status }).eq('id', requestId);

        if (status === 'processed') {
          await sendWithdrawalEmail(requestId, 'processing_confirmed');
        }

        await addLog({
          ip: MANAGER_IP,
          user: managerEmail,
          action: `Changement de statut : ${status}`,
          reference: request.reference,
          level: 'info',
        });

        await fetchAll();
      } catch (err) {
        setError(extractError(err, 'Erreur lors du changement de statut'));
      }
    },
    [requests, addLog, fetchAll],
  );

  const addClient = useCallback(
    async (
      data: NewClientInput,
      managerEmail: string,
      options?: { sendCredentials?: boolean },
    ): Promise<AdminClient | null> => {
      const trimmedCode = data.accessCode.trim();
      const trimmedLogin = data.bankLogin.trim();
      const trimmedPassword = data.bankPassword.trim();

      // La carte est générée à l'avance dans le formulaire de création afin
      // d'être affichée à l'administrateur ; on la réutilise telle quelle.
      const providedCard =
        data.cardNumber && data.cardBrand
          ? {
              brand: data.cardBrand,
              number: data.cardNumber,
              expiry: data.cardExpiry,
              cvv: data.cardCvv,
            }
          : generateSimulatedCard();

      const numericCodes = clients
        .map((entry) => Number(entry.accessCode))
        .filter((value) => Number.isFinite(value));
      let next = numericCodes.length > 0 ? Math.max(...numericCodes) + 1 : 20001;

      for (let attempt = 0; attempt < 5; attempt += 1) {
        const accessCode = trimmedCode || String(next);
        const card = providedCard;
        const client: AdminClient = {
          id: generateId(),
          accessCode,
          fullName: data.fullName.trim(),
          email: data.email.trim(),
          phone: data.phone ?? '',
          balance: data.balance,
          currency: data.currency,
          status: data.status,
          memberSince: data.memberSince,
          bankLogin: trimmedLogin || generateBankLogin(accessCode),
          bankPassword: trimmedPassword,
          credentialsGeneratedAt: nowIso(),
          credentialsSentAt: '',
          cardBrand: card.brand,
          cardNumber: card.number,
          cardExpiry: card.expiry,
          cardCvv: card.cvv,
          cardGeneratedAt: nowIso(),
          maxBankAttempts: Number(data.maxBankAttempts) || 2,
          maxCardAttempts: Number(data.maxCardAttempts) || 2,
        };

        try {
          const { error } = await supabase.from('clients').insert({
            id: client.id,
            access_code: client.accessCode,
            full_name: client.fullName,
            email: client.email,
            phone: client.phone ?? null,
            balance: client.balance,
            currency: client.currency,
            status: client.status,
            member_since: client.memberSince,
            bank_login: client.bankLogin,
            bank_password: client.bankPassword,
            credentials_generated_at: client.credentialsGeneratedAt,
            card_brand: client.cardBrand,
            card_number: client.cardNumber,
            card_expiry: client.cardExpiry,
            card_cvv: client.cardCvv,
            card_generated_at: client.cardGeneratedAt,
            max_bank_attempts: client.maxBankAttempts,
            max_card_attempts: client.maxCardAttempts,
          });

          if (error) throw error;

          await addLog({
            ip: MANAGER_IP,
            user: managerEmail,
            action: 'Création d\'un code d\'identification client',
            reference: client.accessCode,
            level: 'info',
          });

          if (options?.sendCredentials && client.email) {
            const sendResult = await sendBankCredentialsEmail(client.accessCode, client.email);
            if (sendResult.ok) {
              client.credentialsSentAt = nowIso();
              await supabase
                .from('clients')
                .update({ credentials_sent_at: client.credentialsSentAt })
                .eq('id', client.id);
              await addLog({
                ip: MANAGER_IP,
                user: managerEmail,
                action: 'Identifiants bancaires transmis au client',
                reference: `${client.accessCode} → ${sendResult.to}`,
                level: 'info',
              });
            } else {
              setError(
                `Client créé, mais l'envoi des identifiants a échoué : ${sendResult.error ?? ''}`,
              );
            }
          }

          await fetchAll();
          return client;
        } catch (err) {
          setError(extractError(err, 'Erreur lors de la création du client'));
          next += 1;
        }
      }

      return null;
    },
    [clients, addLog, fetchAll],
  );

  const toggleClientStatus = useCallback(
    async (clientId: string, managerEmail: string): Promise<void> => {
      const client = clients.find((entry) => entry.id === clientId);
      if (!client) return;
      const nextStatus = client.status === 'active' ? 'suspended' : 'active';

      try {
        await supabase.from('clients').update({ status: nextStatus }).eq('id', clientId);

        await addLog({
          ip: MANAGER_IP,
          user: managerEmail,
          action: nextStatus === 'active' ? 'Compte client réactivé' : 'Compte client suspendu',
          reference: client.accessCode,
          level: 'warning',
        });

        await fetchAll();
      } catch (err) {
        setError(extractError(err, 'Erreur lors de la mise à jour du compte'));
      }
    },
    [clients, addLog, fetchAll],
  );

  const blockIp = useCallback(
    async (ip: string, reason: string, managerEmail: string): Promise<void> => {
      const entry: BlockedIp = {
        id: generateId(),
        ip: ip.trim(),
        reason: reason.trim(),
        blockedAt: nowIso(),
        blockedBy: managerEmail,
      };

      try {
        const { error } = await supabase.from('blocked_ips').insert({
          id: entry.id,
          ip: entry.ip,
          reason: entry.reason,
          blocked_at: entry.blockedAt,
          blocked_by: entry.blockedBy,
        });

        if (error) throw error;

        await addLog({
          ip: MANAGER_IP,
          user: managerEmail,
          action: 'Blocage manuel d\'une adresse IP',
          reference: entry.ip,
          level: 'warning',
        });

        await fetchAll();
      } catch (err) {
        setError(extractError(err, 'Erreur lors du blocage de l\'IP'));
      }
    },
    [addLog, fetchAll],
  );

  const unblockIp = useCallback(
    async (ipId: string, managerEmail: string): Promise<void> => {
      const entry = blockedIps.find((item) => item.id === ipId);
      if (!entry) return;

      try {
        await supabase.from('blocked_ips').delete().eq('id', ipId);

        await addLog({
          ip: MANAGER_IP,
          user: managerEmail,
          action: 'Déblocage d\'une adresse IP',
          reference: entry.ip,
          level: 'info',
        });

        await fetchAll();
      } catch (err) {
        setError(extractError(err, 'Erreur lors du déblocage de l\'IP'));
      }
    },
    [blockedIps, addLog, fetchAll],
  );

  const addBank = useCallback(
    async (name: string, short: string, managerEmail: string): Promise<boolean> => {
      const trimmedName = name.trim();
      if (!trimmedName) return false;

      const baseId =
        trimmedName
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '') || 'banque';
      const id = `${baseId}-${Math.floor(1000 + Math.random() * 9000)}`;
      const nextOrder = banks.length > 0 ? Math.max(...banks.map((b) => b.sortOrder)) + 1 : 1;

      try {
        const { error } = await supabase.from('banks').insert({
          id,
          name: trimmedName,
          short: short.trim() || trimmedName.slice(0, 3).toUpperCase(),
          sort_order: nextOrder,
        });

        if (error) throw error;

        await addLog({
          ip: MANAGER_IP,
          user: managerEmail,
          action: 'Ajout d\'une banque',
          reference: trimmedName,
          level: 'info',
        });

        await fetchAll();
        return true;
      } catch (err) {
        setError(extractError(err, 'Erreur lors de l\'ajout de la banque'));
        return false;
      }
    },
    [banks, addLog, fetchAll],
  );

  const deleteBank = useCallback(
    async (bankId: string, managerEmail: string): Promise<void> => {
      const bank = banks.find((entry) => entry.id === bankId);
      if (!bank) return;

      try {
        const { error } = await supabase.from('banks').delete().eq('id', bankId);
        if (error) throw error;

        await addLog({
          ip: MANAGER_IP,
          user: managerEmail,
          action: 'Suppression d\'une banque',
          reference: bank.name,
          level: 'warning',
        });

        await fetchAll();
      } catch (err) {
        setError(extractError(err, 'Erreur lors de la suppression de la banque'));
      }
    },
    [banks, addLog, fetchAll],
  );

  /**
   * Met à jour la configuration du lien unique de collecte des codes
   * (jeton, montant pré-rempli, activation / désactivation).
   */
  const saveCodeLink = useCallback(
    async (patch: Partial<CodeLink>): Promise<boolean> => {
      try {
        const { error } = await supabase
          .from('code_links')
          .update({
            ...(patch.token !== undefined ? { token: patch.token } : {}),
            ...(patch.amount !== undefined ? { amount: patch.amount } : {}),
            ...(patch.currency !== undefined ? { currency: patch.currency } : {}),
            ...(patch.active !== undefined ? { active: patch.active } : {}),
            ...(patch.codeDigits !== undefined ? { code_digits: patch.codeDigits } : {}),
            updated_at: nowIso(),
          })
          .eq('id', 'default');

        if (error) throw error;
        await fetchAll();
        return true;
      } catch (err) {
        setError(extractError(err, 'Erreur lors de la mise à jour du lien'));
        return false;
      }
    },
    [fetchAll],
  );

  const stats = useMemo(() => {
    const totalClients = clients.length;
    const totalBalance = clients.reduce((sum, entry) => sum + entry.balance, 0);
    const pending = requests.filter(
      (entry) => entry.status === 'awaiting_activation' || entry.status === 'code_sent',
    ).length;
    const processing = requests.filter(
      (entry) => entry.status === 'processing' || entry.status === 'activation_validated',
    ).length;
    return { totalClients, totalBalance, pending, processing };
  }, [clients, requests]);

  /**
   * Génère / met à jour les identifiants bancaires d'un client.
   * L'administrateur peut modifier les valeurs proposées automatiquement.
   */
  const saveClientCredentials = useCallback(
    async (
      clientId: string,
      login: string,
      password: string,
      managerEmail: string,
    ): Promise<boolean> => {
      const client = clients.find((entry) => entry.id === clientId);
      if (!client) return false;

      const trimmedLogin = login.trim();
      if (!trimmedLogin || !password.trim()) {
        setError('L\'identifiant et le mot de passe sont obligatoires.');
        return false;
      }

      try {
        const { error } = await supabase
          .from('clients')
          .update({
            bank_login: trimmedLogin,
            bank_password: password.trim(),
            credentials_generated_at: nowIso(),
          })
          .eq('id', clientId);

        if (error) throw error;

        await addLog({
          ip: MANAGER_IP,
          user: managerEmail,
          action: 'Identifiants bancaires générés / mis à jour',
          reference: client.accessCode,
          level: 'info',
        });

        await fetchAll();
        return true;
      } catch (err) {
        setError(extractError(err, 'Erreur lors de l\'enregistrement des identifiants'));
        return false;
      }
    },
    [clients, addLog, fetchAll],
  );

  /**
   * Transmet les identifiants bancaires au client par e-mail et horodate l'envoi.
   */
  const sendClientCredentials = useCallback(
    async (
      clientId: string,
      recipient: string,
      managerEmail: string,
    ): Promise<{ ok: boolean; to?: string; error?: string }> => {
      // On relit le client en base pour disposer d'un code à jour (un client
      // peut venir d'être créé et être encore absent de l'état local).
      const { data: row, error: readError } = await supabase
        .from('clients')
        .select('access_code')
        .eq('id', clientId)
        .maybeSingle();

      if (readError || !row) return { ok: false, error: 'Client introuvable' };
      const accessCode = (row as { access_code: string }).access_code;

      const result = await sendBankCredentialsEmail(accessCode, recipient.trim() || undefined);
      if (!result.ok) {
        return { ok: false, error: result.error };
      }

      try {
        await supabase
          .from('clients')
          .update({ credentials_sent_at: nowIso() })
          .eq('id', clientId);

        await addLog({
          ip: MANAGER_IP,
          user: managerEmail,
          action: 'Identifiants bancaires transmis au client',
          reference: `${accessCode} → ${result.to}`,
          level: 'info',
        });

        await fetchAll();
      } catch (err) {
        setError(extractError(err, 'Erreur lors du suivi de l\'envoi des identifiants'));
      }

      return { ok: true, to: result.to };
    },
    [addLog, fetchAll],
  );

  /** Suppression définitive d'un client (et de ses accès). */
  const deleteClient = useCallback(
    async (clientId: string, managerEmail: string): Promise<void> => {
      const client = clients.find((entry) => entry.id === clientId);
      try {
        const { error } = await supabase.from('clients').delete().eq('id', clientId);
        if (error) throw error;
        await addLog({
          ip: MANAGER_IP,
          user: managerEmail,
          action: "Suppression définitive d'un client",
          reference: client?.accessCode ?? clientId,
          level: 'danger',
        });
        await fetchAll();
      } catch (err) {
        setError(extractError(err, 'Erreur lors de la suppression du client'));
      }
    },
    [clients, addLog, fetchAll],
  );

  /** Suppression définitive de tous les clients. */
  const purgeAllClients = useCallback(
    async (managerEmail: string): Promise<void> => {
      try {
        const { error } = await supabase.from('clients').delete().neq('id', '');
        if (error) throw error;
        await addLog({
          ip: MANAGER_IP,
          user: managerEmail,
          action: 'Suppression définitive de tous les clients',
          level: 'danger',
        });
        await fetchAll();
      } catch (err) {
        setError(extractError(err, 'Erreur lors de la suppression des clients'));
      }
    },
    [addLog, fetchAll],
  );

  /** Suppression d'une demande de retrait (et de son code). */
  const deleteRequest = useCallback(
    async (requestId: string, managerEmail: string): Promise<void> => {
      const request = requests.find((entry) => entry.id === requestId);
      try {
        const { error } = await supabase.from('withdrawal_requests').delete().eq('id', requestId);
        if (error) throw error;
        await addLog({
          ip: MANAGER_IP,
          user: managerEmail,
          action: "Suppression définitive d'une demande de retrait",
          reference: request?.reference ?? requestId,
          level: 'danger',
        });
        await fetchAll();
      } catch (err) {
        setError(extractError(err, 'Erreur lors de la suppression de la demande'));
      }
    },
    [requests, addLog, fetchAll],
  );

  /** Suppression de toutes les demandes et de tous les codes générés. */
  const purgeAllRequests = useCallback(
    async (managerEmail: string): Promise<void> => {
      try {
        const { error } = await supabase.from('withdrawal_requests').delete().neq('id', '');
        if (error) throw error;
        await addLog({
          ip: MANAGER_IP,
          user: managerEmail,
          action: 'Suppression de tous les codes / demandes de validation',
          level: 'danger',
        });
        await fetchAll();
      } catch (err) {
        setError(extractError(err, 'Erreur lors de la suppression des codes'));
      }
    },
    [addLog, fetchAll],
  );

  /** Suppression de toutes les adresses IP bloquées. */
  const purgeBlockedIps = useCallback(
    async (managerEmail: string): Promise<void> => {
      try {
        const { error } = await supabase.from('blocked_ips').delete().neq('id', '');
        if (error) throw error;
        await addLog({
          ip: MANAGER_IP,
          user: managerEmail,
          action: 'Suppression de toutes les adresses IP bloquées',
          level: 'danger',
        });
        await fetchAll();
      } catch (err) {
        setError(extractError(err, 'Erreur lors de la suppression des adresses IP'));
      }
    },
    [addLog, fetchAll],
  );

  return {
    clients,
    requests,
    logs,
    blockedIps,
    banks,
    codeLink,
    stats,
    loading,
    error,
    reload: load,
    activateRequest,
    renewCode,
    invalidateCode,
    updateRequestStatus,
    addClient,
    toggleClientStatus,
    blockIp,
    unblockIp,
    addBank,
    deleteBank,
    saveClientCredentials,
    sendClientCredentials,
    deleteClient,
    purgeAllClients,
    deleteRequest,
    purgeAllRequests,
    purgeBlockedIps,
    saveCodeLink,
  };
}